import { useCallback, useEffect, useRef } from "react";

/**
 * usePolling — run `fn` now and then every `intervalMs`, but only while the tab can use
 * the answer.
 *
 * Lifted from gateway/hooks/useConsultHeartbeat.js, whose gate is the one this product
 * already trusts:
 *   - no request while the document is hidden or the browser is offline;
 *   - an IMMEDIATE run when the tab comes back to the foreground or back online — a
 *     reader returning to the inbox must not wait a whole interval to see what landed;
 *   - an in-flight guard that EXPIRES after one interval, so a request that never
 *     settles costs one missed tick instead of every future one.
 *
 * A `setTimeout` chain rather than `setInterval`: the next tick is scheduled after the
 * current one resolves, so a slow server is never queued against.
 *
 * `refresh()` runs `fn` right away regardless of the gate — it is for the moments the
 * caller KNOWS the server changed (they just sent a message). Overlap with a scheduled
 * tick is harmless: every consumer merges by id and cursors are server-minted.
 *
 * `fn` is read through a ref, so callers may pass an inline async function.
 */
export default function usePolling(fn, { intervalMs = 60000, enabled = true } = {}) {
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const inFlightSinceRef = useRef(null);
  const timerRef = useRef(null);
  const stoppedRef = useRef(false);

  const canRun = () =>
    typeof document !== "undefined"
    && document.visibilityState === "visible"
    && (typeof navigator === "undefined" || navigator.onLine !== false);

  const run = useCallback(async ({ force = false } = {}) => {
    if (stoppedRef.current) return;
    if (!force && !canRun()) return;
    const since = inFlightSinceRef.current;
    if (!force && since !== null && Date.now() - since < intervalMs) return;
    inFlightSinceRef.current = Date.now();
    try {
      await fnRef.current();
    } catch {
      // The consumer surfaces its own read failure (LoadFailure); the loop keeps going.
    } finally {
      inFlightSinceRef.current = null;
    }
  }, [intervalMs]);

  useEffect(() => {
    if (!enabled) return undefined;
    stoppedRef.current = false;

    const schedule = () => {
      timerRef.current = setTimeout(async () => {
        await run();
        if (!stoppedRef.current) schedule();
      }, intervalMs);
    };
    const onResume = () => { if (canRun()) run(); };

    document.addEventListener("visibilitychange", onResume);
    window.addEventListener("online", onResume);
    run();
    schedule();

    return () => {
      stoppedRef.current = true;
      clearTimeout(timerRef.current);
      document.removeEventListener("visibilitychange", onResume);
      window.removeEventListener("online", onResume);
    };
  }, [enabled, intervalMs, run]);

  const refresh = useCallback(() => run({ force: true }), [run]);
  return { refresh };
}
